import { Close, Down } from '@icon-park/react';
import { type MemoExoticComponent, memo, type ReactElement, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useBoolState } from '@didaskill/ui/utils';

import { Button } from '../../general/button/Button';
import { Typography } from '../../general/typography/Typography';
import { Dropdown } from '../../navigation/dropdown/Dropdown';
import messages from './BulkActionsBar.messages';
import * as styles from './BulkActionsBar.styles';

export type BulkAction = {
  /** Stable identifier used as React key. */
  key: string;
  /** Visible label in the actions dropdown. */
  label: ReactNode;
  /** Called when the action is chosen. */
  onClick: () => void;
  /** Optional IconPark icon shown before the label. */
  icon?: ReactElement;
  /** Disables the dropdown item. */
  disabled?: boolean;
};

export type BulkShortcutAction = {
  /** Stable identifier used as React key. */
  key: string;
  /** IconPark icon rendered in the square shortcut button. */
  icon: ReactElement;
  /** Called when the shortcut is clicked. */
  onClick: () => void;
  /** Accessible name of the icon-only button. */
  ariaLabel: string;
  /** Disables the shortcut button. */
  disabled?: boolean;
};

export type BulkActionsBarProps = {
  /** Number of currently selected rows. The bar is hidden when this is 0. */
  selectedCount: number;
  /** Configurable actions listed in the Actions dropdown. */
  actions?: BulkAction[];
  /** Optional icon buttons shown next to the Actions dropdown. */
  shortcutActions?: BulkShortcutAction[];
  /** Clears the current selection and hides the bar. */
  onClear: () => void;
  /** Override the Actions dropdown trigger label. */
  actionsLabel?: ReactNode;
};

const DEFAULT_ACTIONS: BulkAction[] = [];
const DEFAULT_SHORTCUT_ACTIONS: BulkShortcutAction[] = [];

/**
 * Floating toolbar shown when table rows are selected.
 * Renders the selection count, a configurable actions dropdown, optional shortcut buttons and a
 * dismiss control.
 * @param props Component props.
 * @returns The bulk actions bar, or null when nothing is selected.
 */
export const BulkActionsBarBase = ({
  selectedCount,
  actions = DEFAULT_ACTIONS,
  shortcutActions = DEFAULT_SHORTCUT_ACTIONS,
  onClear,
  actionsLabel = null,
}: BulkActionsBarProps): JSX.Element | null => {
  const intl = useIntl();
  const [isActionsOpen, , onCloseActions, onToggleActions] = useBoolState(false);

  if (selectedCount <= 0) {
    return null;
  }

  return (
    <div css={styles.bar} role="toolbar" aria-label={intl.formatMessage(messages.ACTIONS)}>
      <Typography variant={Typography.VARIANTS.BODY_MEDIUM_REGULAR}>
        <FormattedMessage {...messages.SELECTED_COUNT} values={{ count: selectedCount }} />
      </Typography>

      <div css={styles.actions}>
        {actions.length > 0 && (
          <Dropdown
            visible={isActionsOpen}
            onClickOutside={onCloseActions}
            placement="top"
            menu={
              <Dropdown.List css={styles.actionsList}>
                {actions.map((action) => (
                  <Dropdown.ListItem
                    key={action.key}
                    value={action.key}
                    icon={action.icon}
                    label={action.label}
                    isDisabled={action.disabled}
                    onClick={() => {
                      onCloseActions();
                      action.onClick();
                    }}
                  />
                ))}
              </Dropdown.List>
            }
          >
            <Button
              outline
              variant={Button.VARIANTS.PRIMARY}
              icon={<Down />}
              iconPosition={Button.ICON_POSITIONS.RIGHT}
              onClick={onToggleActions}
              isActive={isActionsOpen}
            >
              {actionsLabel ?? <FormattedMessage {...messages.ACTIONS} />}
            </Button>
          </Dropdown>
        )}

        {shortcutActions.map((shortcutAction) => (
          <Button
            key={shortcutAction.key}
            outline
            variant={Button.VARIANTS.NEUTRAL}
            icon={shortcutAction.icon}
            onClick={shortcutAction.onClick}
            disabled={shortcutAction.disabled}
            aria-label={shortcutAction.ariaLabel}
          />
        ))}
      </div>

      <Button
        icon={<Close />}
        onClick={onClear}
        aria-label={intl.formatMessage(messages.CLEAR_SELECTION)}
      />
    </div>
  );
};

BulkActionsBarBase.displayName = 'BulkActionsBar';

export const BulkActionsBar: MemoExoticComponent<typeof BulkActionsBarBase> =
  memo(BulkActionsBarBase);
